import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Modal,
} from 'react-bootstrap';
import _ from 'lodash';
import styled from 'styled-components';
import Input from 'react-phone-number-input/input';
import { useMutation } from '@apollo/client';
import { useAlert } from 'react-alert';
import * as Yup from 'yup';
import {
  Form,
  Formik,
} from 'formik';

import { GQL_GET_ME, GQL_ME_UPDATE, GQL_SEND_PROMO_REQUEST } from '../graphql/queries';
import { useTranslation } from './hooks/useTranslation';

const NotificationModal = ({
  show,
  setting,
  onHide,
  refetchQueries,
}) => {
  const alert = useAlert();
  const { t } = useTranslation();
  const update = (cache) => {
    const data = cache.readQuery({ query: GQL_GET_ME });
    data.me.signed_up = true;
    cache.writeQuery({
      query: GQL_GET_ME,
      data,
    });
  };

  const [loading, setLoading] = useState(false);
  const [updateMe] = useMutation(GQL_ME_UPDATE, { update });
  const [sendRequest] = useMutation(GQL_SEND_PROMO_REQUEST);

  const close = async (confirmed) => {
    await sendRequest({
      variables: { confirmed },
      refetchQueries,
    });
    onHide();
  };

  const handleUpdate = async (values, {
    setSubmitting,
    setErrors,
  }) => {
    try {
      if (!values.phone) {
        alert.show(t('phone_required', { ns: 'notification_modal' }), { type: 'danger' });
        return false;
      }
      setLoading(true);
      const response = await updateMe({
        variables: { me: { phone: values.phone } },
      });
      setLoading(false);
      const errors = _.get(response, 'errors.[0].extensions', {});
      if (_.isEmpty(errors)) {
        await close('yes');
        alert.show(t('success', { ns: 'notification_modal' }), { type: 'success' });
        return true;
      }
      setSubmitting(false);
      setErrors(errors);
      return false;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      return false;
    }
  };

  const initialValues = {
    phone: _.get(setting, 'phone', ''),
  };

  const phoneRegExp = /^\+\d{7,16}$/;

  const validationSchema = Yup.object()
    .shape({
      // eslint-disable-next-line quotes
      phone: Yup.string()
        .matches(phoneRegExp, t('phone', { ns: 'validate' }))
        .required(t('required', { ns: 'validate' }))
        .nullable(),
    });

  return (
    <Modal
      id="notification"
      size="lg"
      show={show}
      onHide={onHide}
      backdrop="static"
      centered
    >
      <div className="p-2 px-md-5 py-md-3 mt-5">
        <Modal.Body className="p-3">
          <Formik
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleUpdate}
            enableReinitialize
          >
            {({
              isSubmitting,
              values,
              setFieldValue,
              errors,
            }) => (
              <Form>
                <Description dangerouslySetInnerHTML={{ __html: setting.value }} />
                <Input
                  name="phone"
                  placeholder={t('placeholder', { ns: 'notification_modal' })}
                  className={`form-control-lg form-control w-100 ${_.get(errors, 'phone')
                    ? 'is-invalid' : ''}`}
                  disabled={isSubmitting || loading}
                  value={_.get(values, 'phone', '') ? values.phone.replace('+8', '+7') : ''}
                  onChange={(value) => setFieldValue('phone', value)}
                />
                {_.get(errors, 'phone') && (
                  <div className="invalid-feedback d-block">{t('required', { ns: 'notification_modal' })}</div>
                )}
                <div className="row mt-5 w-100 mx-0 pb-5 justify-content-between">
                  <Button
                    type="submit"
                    className="me-0 me-md-4 col-sm-12 col-lg-auto mb-4 mb-md-0"
                    size="lg"
                    variant="primary"
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting ? t('await', { ns: 'action' }) : t('send', { ns: 'notification_modal' })}
                  </Button>
                  <Button
                    type="button"
                    className="me-0 me-md-4 col-sm-12 col-lg-auto px-0"
                    onClick={() => close('no')}
                    size="lg"
                    variant="link"
                    disabled={isSubmitting || loading}
                  >
                    {t('no', { ns: 'notification_modal' })}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </div>
    </Modal>
  );
};

const Description = styled.div`
  display: block;
`;

NotificationModal.defaultProps = {
  show: false,
  refetchQueries: [],
};

NotificationModal.propTypes = {
  refetchQueries: PropTypes.array,
  show: PropTypes.bool,
  setting: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]).isRequired,
  onHide: PropTypes.func.isRequired,
};

export default NotificationModal;
